<template>
  <div>
    <nav class="breadcrumb mt-2" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/">Home</router-link></li>
        <li class="is-active">
          <a href="#" aria-current="page">{{ postTitle }}</a>
        </li>
      </ul>
    </nav>
    <div v-if="postContent">
      <mermaid-markdown :source="postContent" />
    </div>
    <div v-if="relatedPosts.length" class="related-articles">
      <h3 class="related-articles-title">Related Articles</h3>
      <div class="columns is-multiline">
        <div
          class="column is-12-mobile is-4-tablet"
          v-for="post in relatedPosts"
          :key="post.slug"
        >
          <div class="blog-card">
            <router-link :to="`/blog/post/${post.slug}`" class="blog-card-link">
              <h4 class="blog-card-title">{{ post.title }}</h4>
              <p class="blog-card-excerpt">{{ post.excerpt }}</p>
              <div class="blog-card-tags">
                <b-tag
                  v-for="tag in post.tags"
                  :key="tag"
                  class="blog-card-tag"
                  type="is-info"
                  size="is-small"
                  :closable="false"
                  >{{ tag }}</b-tag
                >
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MermaidMarkdown from "./MermaidMarkdown.vue";

const markdownFiles = require.context("./posts/", false, /\.md$/);

export default {
  components: { MermaidMarkdown },
  data() {
    return {
      postContent: "",
      postTitle: "",
      currentPostTags: [],
      currentSlug: "",
      allPosts: [],
    };
  },
  computed: {
    relatedPosts() {
      if (!this.allPosts.length || !this.currentSlug) return [];

      const otherPosts = this.allPosts.filter(
        (p) => p.slug !== this.currentSlug
      );

      const scored = otherPosts.map((post) => {
        const matchingTags = post.tags.filter((tag) =>
          this.currentPostTags.includes(tag)
        ).length;
        return { ...post, score: matchingTags };
      });

      scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return new Date(b.date) - new Date(a.date);
      });

      let related = scored.slice(0, 3);

      if (related.length < 3) {
        const existingSlugs = new Set(related.map((p) => p.slug));
        const latest = otherPosts
          .filter((p) => !existingSlugs.has(p.slug))
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 3 - related.length);
        related = [...related, ...latest];
      }

      return related;
    },
  },
  async created() {
    await this.loadAllPosts();
  },
  watch: {
    "$route.params.slug": {
      immediate: true,
      async handler() {
        await this.loadPostData();
        document.title = this.postTitle
          ? `${this.postTitle} - David Mouat Thomson`
          : "David Mouat Thomson";
      },
    },
  },
  methods: {
    async loadAllPosts() {
      const files = markdownFiles.keys();
      const posts = await Promise.all(
        files.map(async (file) => {
          const slug = file.replace("./", "").replace(".md", "");
          const content = await import(`./posts/${slug}.md`);
          const frontmatterRegex = /^---\n([\s\S]+?)\n---\n([\s\S]*)$/;
          const match = content.default.match(frontmatterRegex);

          let title = "Untitled";
          let excerpt = "";
          let date = "";
          let tags = [];

          if (match) {
            const frontmatter = match[1];
            const titleMatch = frontmatter.match(/title:\s*(.+)/);
            if (titleMatch) title = titleMatch[1];

            const excerptMatch = frontmatter.match(/excerpt:\s*(.+)/);
            if (excerptMatch) excerpt = excerptMatch[1];

            const dateMatch = frontmatter.match(/date:\s*(.+)/);
            if (dateMatch) date = dateMatch[1];

            const tagsMatch = frontmatter.match(/tags:\s*\[(.+)\]/);
            if (tagsMatch) {
              try {
                tags = JSON.parse(`[${tagsMatch[1]}]`);
              } catch (e) {
                tags = [];
              }
            }
          }

          return { slug, title, excerpt, date, tags };
        })
      );

      this.allPosts = posts;
    },
    async loadPostData() {
      const slug = this.$route.params.slug;
      this.currentSlug = slug;

      if (window.__PRERENDERED__) {
        this.postTitle = window.__PRERENDERED_TITLE__ || this.postTitle;
        const prerenderedEl = document.getElementById("prerendered-content");
        if (prerenderedEl) {
          this.postContent = prerenderedEl.innerHTML;
          prerenderedEl.style.display = "block";
          return;
        }
      }

      const post = await import(`./posts/${slug}.md`);
      const frontmatterRegex = /^---\n([\s\S]+?)\n---\n([\s\S]*)$/;
      const match = post.default.match(frontmatterRegex);
      this.postContent = match ? match[2] : post.default;
      this.postTitle =
        match && match[1].match(/title:\s*(.+)/)
          ? match[1].match(/title:\s*(.+)/)[1]
          : "Untitled";
      this.currentPostTags =
        match && match[1].match(/tags:\s*\[(.+)\]/)
          ? JSON.parse(`[${match[1].match(/tags:\s*\[(.+)\]/)[1]}]`)
          : [];
    },
  },
};
</script>

<style lang="scss">

.breadcrumbs {
  margin-bottom: 1rem;
  font-size: 0.9rem;
}
p{
  margin-bottom: 1rem;
  line-height: 1.5em;
}

.breadcrumbs a {
  text-decoration: none;
  color: var(--primary-text-color, #333);
}

.breadcrumbs span {
  color: var(--secondary-text-color, #666);
}

h1 {
  padding-top: 1rem;
  font-size: 2rem;
  font-weight: bold;
  color: var(--primary-text-color, #333);
}

h2 {
  font-size: 1.75rem;
  font-weight: bold;
  padding-top: 0.75rem;
  color: var(--primary-text-color, #333);
}

h3 {
  font-size: 1.5rem;
  font-weight: bold;
  padding-top: 0.5rem;
  color: var(--primary-text-color, #333);
}

h4,
h5,
h6 {
  font-size: 1.25rem;
  font-weight: bold;
  padding-top: 0.5rem;
  color: var(--primary-text-color, #333);
}

ul {
  list-style-type: disc;
  margin-bottom: 1rem;
  margin-left: 1rem;
}

ol {
  list-style-type: decimal;
  margin-left: 2rem;
  margin-bottom: 1rem;
}

li {
  margin-bottom: 0.5rem;
}
.breadcrumb {
  ul {
      margin-left: 0;
  }
}

blockquote {
  border-left: 4px solid var(--secondary-text-color, #666);
  padding-left: 1rem;
  margin-left: 0;
  margin-right: 0;
  color: var(--secondary-text-color, #666);
  font-style: italic;
}

code {
  background-color: var(--code-bg-color, #f4f4f4);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: monospace;
}

pre {
  background-color: var(--code-bg-color, #f4f4f4);
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
}

hr {
  border: none;
  border-top: 1px solid var(--secondary-text-color, #666);
  margin: 2rem 0;
}

.blog-post-image {
  max-width: 100%;
  height: auto;
  margin-bottom: 1rem;
  border: 1px solid var(--secondary-text-color, #666);
  border-radius: 15px;
  max-height: 500px;
  cursor: zoom-in;
}

.blog-post-image:active {
  transform: scale(2.05);
  transform-origin: center;
  transition: transform 0.5s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.caption {
  font-size: 0.9rem;
  color: var(--secondary-text-color, #666);
  text-align: center;
  margin-top: -0.5rem;
}
table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}
td,
th {
  border: 1px solid var(--secondary-text-color, #666);
  padding: 0.5rem;
  text-align: left;
}
th {
  background-color: var(--table-header-bg-color, #f0f0f0);
  font-weight: bold;
}
td {
  background-color: var(--table-row-bg-color, #ffffff);
}
.related-articles {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 2px solid var(--secondary-text-color, #666);
}
.related-articles-title {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  color: var(--primary-text-color, #333);
}
.related-articles .blog-card {
  background-color: var(--card-bg-color, #ffffff);
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
}
.related-articles .blog-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
}
.related-articles .blog-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
  padding: 1.5rem;
}
.related-articles .blog-card-title {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: var(--primary-text-color, #333333);
}
.related-articles .blog-card-excerpt {
  font-size: 0.9rem;
  color: var(--secondary-text-color, #666666);
  line-height: 1.5;
  margin-bottom: 0.75rem;
}
.related-articles .blog-card-tag {
  margin: 0 0.25rem;
}
</style>
